import { CalendarIcon } from 'lucide-react';
import { useState } from 'react';
import type { DateRange as DayPickerDateRange } from 'react-day-picker';

import { Calendar } from '@/components/shadcn/ui/calendar';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/shadcn/ui/popover';
import { cn } from '@/lib/cn';
import { DATE_FORMAT, getTimezoneDayjs } from '@/utils/time';

import { useFieldContext } from './form';
import { getFieldError } from './get-field-error';

interface FormDateRangeFieldProps {
  placeholder?: string;
  showErrorMessage?: boolean;
  numberOfMonths?: number;
}

export function FormDateRangeField({
  placeholder = 'Pick a date range',
  showErrorMessage = true,
  numberOfMonths = 2,
}: FormDateRangeFieldProps) {
  const field = useFieldContext<{ startDate: string; endDate: string }>();
  const [open, setOpen] = useState(false);

  const value = field.state.value;

  // YYYY-MM-DD string → Date 변환 (timezone 고려)
  const selected: DayPickerDateRange | undefined =
    value.startDate && value.endDate
      ? {
          from: getTimezoneDayjs(value.startDate).toDate(),
          to: getTimezoneDayjs(value.endDate).toDate(),
        }
      : undefined;

  const handleSelect = (range: DayPickerDateRange | undefined) => {
    if (!range) return;

    const startDate = range.from
      ? getTimezoneDayjs(range.from.toISOString()).format(DATE_FORMAT.base)
      : '';
    const endDate = range.to
      ? getTimezoneDayjs(range.to.toISOString()).format(DATE_FORMAT.base)
      : '';

    field.handleChange({ startDate, endDate });
  };

  // 표시 텍스트
  const displayText =
    value.startDate && value.endDate
      ? `${getTimezoneDayjs(value.startDate).format(DATE_FORMAT.readable)} - ${getTimezoneDayjs(value.endDate).format(DATE_FORMAT.readable)}`
      : placeholder;

  // 캘린더 기본 표시 월 (선택된 범위의 시작 월 기준)
  const defaultMonth = value.startDate
    ? getTimezoneDayjs(value.startDate).toDate()
    : undefined;

  return (
    <div className="flex flex-col gap-2">
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button
            type="button"
            className={cn(
              'flex h-9 w-full items-center gap-2 rounded-md border border-input bg-background px-3 py-2 text-left text-sm shadow-xs transition-colors hover:bg-accent hover:text-accent-foreground',
              !value.startDate && 'text-muted-foreground',
            )}
          >
            <CalendarIcon className="size-4 shrink-0" />
            {displayText}
          </button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0" align="start">
          <Calendar
            mode="range"
            defaultMonth={defaultMonth}
            selected={selected}
            onSelect={handleSelect}
            numberOfMonths={numberOfMonths}
          />
        </PopoverContent>
      </Popover>
      {showErrorMessage && field.state.meta.errors.length > 0 && (
        <span className="text-destructive text-sm">
          {getFieldError(field.state.meta.errors[0])}
        </span>
      )}
    </div>
  );
}
